<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import { useUpgradeModal } from '@/composables/useUpgradeModal'
import { useClaimTaxBack } from '@/composables/useClaimTaxBack'
import PaidPlanBadge from '@/components/dashboard/ui/PaidPlanBadge.vue'
import SaSpinner from '@/components/dashboard/ui/SaSpinner.vue'
import DashboardModal from '@/components/dashboard/ui/DashboardModal.vue'

const {
  isOpen,
  step,
  checkoutError,
  nameError,
  isProcessing,
  paymentElementReady,
  expressCheckoutAvailable,
  selectedPaymentMethod,
  cardholderName,
  cardholderCountry,
  isMockCheckout,
  closeUpgradeModal,
  startCheckout,
  mountCheckoutElements,
  confirmPayment,
  clearNameError,
  confirmMockPayment,
  finishSuccess,
  backToPitch,
} = useUpgradeModal()

const paymentElementContainer = ref<HTMLElement | null>(null)
const expressCheckoutContainer = ref<HTMLElement | null>(null)

// Claim-aware pitch: the claim guide is a listed feature, with the amount
// only when we already have an estimate.
const { isClaimAvailable, estimate } = useClaimTaxBack()

const focusCardholderCountry = (event: KeyboardEvent) => {
  if (event.isComposing) return
  event.preventDefault()
  const country = document.getElementById(
    'upgrade-cardholder-country'
  ) as HTMLSelectElement | null
  if (country?.isConnected) country.focus()
}

// Mount the Payment Element once the checkout markup exists (real flow only). The
// checkout step stays mounted during confirmation so a decline never loses card entry.
watch(
  () => isOpen.value && step.value === 'checkout' && !isMockCheckout,
  async (shouldMount) => {
    if (!shouldMount) return
    await nextTick()
    if (paymentElementContainer.value && expressCheckoutContainer.value) {
      await mountCheckoutElements(
        paymentElementContainer.value,
        expressCheckoutContainer.value
      )
    }
  }
)

// Billing countries Stripe's Checkout SDK accepts (its own validation list), labelled
// via the platform's region names and sorted for the select. GB is the default.
const STRIPE_BILLING_COUNTRY_CODES =
  `AC AD AE AF AG AI AL AM AO AQ AR AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO
   BQ BR BS BT BV BW BY BZ CA CD CF CG CH CI CK CL CM CN CO CR CV CW CY CZ DE DJ DK DM DO
   DZ EC EE EG EH ER ES ET FI FJ FK FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT
   GU GW GY HK HN HR HT HU ID IE IL IM IN IO IQ IS IT JE JM JO JP KE KG KH KI KM KN KR KW
   KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MK ML MM MN MO MQ MR MS MT MU
   MV MW MX MY MZ NA NC NE NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT
   PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SZ TA TC
   TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG US UY UZ VA VC VE VG VN VU WF WS XK
   YE YT ZA ZM ZW`.split(/\s+/)
const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })
const billingCountries = STRIPE_BILLING_COUNTRY_CODES.map((code) => ({
  code,
  label: regionNames.of(code) ?? code,
})).sort((a, b) => a.label.localeCompare(b.label))

const modalTitleId = () => {
  if (step.value === 'checkout') return 'upgrade-checkout-title'
  if (step.value === 'success') return 'upgrade-success-title'
  return 'upgrade-modal-title'
}
</script>

<template>
  <DashboardModal
    :is-open="isOpen"
    modal-class="sa-upgrade-modal"
    :aria-labelledby="modalTitleId()"
    :close-disabled="isProcessing"
    @close="closeUpgradeModal"
  >
    <template v-if="step === 'checkout' && !isProcessing" #header-start>
      <button type="button" class="sa-modal-back" @click="backToPitch">
        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fill-rule="evenodd"
            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
            clip-rule="evenodd"
          />
        </svg>
        {{ $t('Dashboard.UpgradeModal.Checkout.Back') }}
      </button>
    </template>

    <div
      v-if="step === 'processing'"
      class="sa-upgrade-modal__processing"
      role="status"
    >
      <SaSpinner size="section" />
      <p>{{ $t('Dashboard.UpgradeModal.Checkout.Processing') }}</p>
    </div>

    <template v-else-if="step === 'checkout' && isMockCheckout">
      <h2 id="upgrade-checkout-title">
        {{ $t('Dashboard.UpgradeModal.Checkout.Title') }}
      </h2>
      <p class="sa-upgrade-modal__checkout-intro">
        {{ $t('Dashboard.UpgradeModal.Checkout.Intro') }}
      </p>
      <div class="sa-mock-checkout" aria-hidden="false">
        <div class="sa-mock-checkout__badge">
          {{ $t('Dashboard.UpgradeModal.Checkout.MockBadge') }}
        </div>
        <div class="sa-mock-checkout__product">
          <span class="sa-mock-checkout__product-name">
            {{ $t('Dashboard.UpgradeModal.Checkout.ProductName') }}
          </span>
          <span class="sa-mock-checkout__product-price">£19.99 / year</span>
        </div>
        <div class="sa-mock-checkout__field sa-mock-checkout__field--card">
          <span class="sa-mock-checkout__field-label">Card number</span>
          <span class="sa-mock-checkout__field-value">4242 4242 4242 4242</span>
        </div>
        <div class="sa-mock-checkout__field">
          <span class="sa-mock-checkout__field-label">Expiry</span>
          <span class="sa-mock-checkout__field-value">12 / 34</span>
        </div>
        <div class="sa-mock-checkout__field">
          <span class="sa-mock-checkout__field-label">CVC</span>
          <span class="sa-mock-checkout__field-value">123</span>
        </div>
        <div class="sa-mock-checkout__field">
          <span class="sa-mock-checkout__field-label">
            {{ $t('Dashboard.UpgradeModal.Checkout.NameOnCard') }}
          </span>
          <span class="sa-mock-checkout__field-value">Jane Appleseed</span>
        </div>
        <div class="sa-mock-checkout__field">
          <span class="sa-mock-checkout__field-label">
            {{ $t('Dashboard.UpgradeModal.Checkout.Country') }}
          </span>
          <span class="sa-mock-checkout__field-value">United Kingdom</span>
        </div>
      </div>
      <p v-if="checkoutError" class="sa-upgrade-modal__error" role="alert">
        {{ checkoutError }}
      </p>
      <p class="sa-upgrade-stripe-footer">
        {{ $t('Dashboard.UpgradeModal.StripeFooter') }}
      </p>
    </template>

    <template v-else-if="step === 'checkout'">
      <h2 id="upgrade-checkout-title">
        {{ $t('Dashboard.UpgradeModal.Checkout.Title') }}
      </h2>
      <!-- Wallet buttons (Apple Pay / Google Pay / PayPal). Always in the DOM so the
           element can mount and resolve availability; v-show reveals it, and the "or"
           divider below, only once a wallet is actually offered on this device. -->
      <div
        v-show="expressCheckoutAvailable"
        ref="expressCheckoutContainer"
        class="sa-upgrade-modal__express-checkout"
      ></div>
      <div
        v-show="expressCheckoutAvailable"
        class="sa-upgrade-modal__or-divider"
      >
        {{ $t('Dashboard.UpgradeModal.Checkout.Or') }}
      </div>
      <div
        v-if="!paymentElementReady"
        class="sa-upgrade-modal__processing"
        role="status"
      >
        <SaSpinner size="section" />
        <p>{{ $t('Dashboard.UpgradeModal.Checkout.LoadingPayment') }}</p>
      </div>
      <div
        ref="paymentElementContainer"
        class="sa-upgrade-modal__payment-element"
      ></div>
      <!-- Stripe's native cardholder-name field is beta-gated, so while card is selected
             we collect the name ourselves — and a name can only be set alongside a billing
             country, so we own that select too. v-show preserves both across tab switches. -->
      <div
        v-show="paymentElementReady && selectedPaymentMethod === 'card'"
        class="sa-form-group"
      >
        <label class="sa-form-label" for="upgrade-cardholder-name">
          {{ $t('Dashboard.UpgradeModal.Checkout.NameOnCard') }}
        </label>
        <input
          id="upgrade-cardholder-name"
          v-model="cardholderName"
          class="sa-input"
          type="text"
          autocomplete="cc-name"
          enterkeyhint="next"
          :aria-invalid="nameError ? 'true' : undefined"
          :aria-describedby="
            nameError ? 'upgrade-cardholder-name-error' : undefined
          "
          @input="clearNameError"
          @keydown.enter="focusCardholderCountry"
        />
        <p
          v-if="nameError"
          id="upgrade-cardholder-name-error"
          class="sa-upgrade-modal__error"
          role="alert"
        >
          {{ nameError }}
        </p>
      </div>
      <div
        v-show="paymentElementReady && selectedPaymentMethod === 'card'"
        class="sa-form-group"
      >
        <label class="sa-form-label" for="upgrade-cardholder-country">
          {{ $t('Dashboard.UpgradeModal.Checkout.Country') }}
        </label>
        <select
          id="upgrade-cardholder-country"
          v-model="cardholderCountry"
          class="sa-input"
          autocomplete="billing country"
        >
          <option
            v-for="country in billingCountries"
            :key="country.code"
            :value="country.code"
          >
            {{ country.label }}
          </option>
        </select>
      </div>
      <p v-if="checkoutError" class="sa-upgrade-modal__error" role="alert">
        {{ checkoutError }}
      </p>
      <!-- Subscription mandate: the element's own terms text is suppressed
           (terms 'never') so this can sit after the whole form, not between
           the card fields and the name/country inputs. -->
      <p v-show="paymentElementReady" class="sa-upgrade-stripe-footer">
        {{ $t('Dashboard.UpgradeModal.Checkout.SubscriptionTerms') }}
      </p>
      <p class="sa-upgrade-stripe-footer">
        {{ $t('Dashboard.UpgradeModal.StripeFooter') }}
      </p>
    </template>

    <template v-else-if="step === 'success'">
      <div class="sa-upgrade-modal__hero-badge" aria-hidden="true">
        <PaidPlanBadge />
      </div>
      <h2 id="upgrade-success-title">
        {{ $t('Dashboard.UpgradeModal.Success.Title') }}
      </h2>
      <p>{{ $t('Dashboard.UpgradeModal.Success.Body') }}</p>
    </template>

    <template v-else>
      <div class="sa-upgrade-modal__hero-badge" aria-hidden="true">
        <PaidPlanBadge />
      </div>
      <h2 id="upgrade-modal-title">{{ $t('Dashboard.UpgradeModal.Title') }}</h2>
      <p>{{ $t('Dashboard.UpgradeModal.Intro') }}</p>
      <ul class="sa-feature-list">
        <li v-for="index in 4" :key="index">
          <div class="sa-check-icon" aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 011.414 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <span>{{ $t(`Dashboard.UpgradeModal.Features.${index}`) }}</span>
        </li>
        <!-- Claim guide bullet, last so it sits closest to the price. Keep
             the wording hedged: we show how to claim, not that HMRC will pay. -->
        <li v-if="isClaimAvailable">
          <div class="sa-check-icon" aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 011.414 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <span>
            {{
              estimate && estimate.claimablePence > 0
                ? $t('Dashboard.UpgradeModal.FeatureClaimGuideWithEstimate')
                : $t('Dashboard.UpgradeModal.FeatureClaimGuide')
            }}
          </span>
        </li>
      </ul>
      <div class="sa-price-block">
        <p
          class="sa-price-deal-row"
          :aria-label="$t('Dashboard.UpgradeModal.PriceDisplay')"
        >
          <span class="sa-price-deal-row__amount" aria-hidden="true">
            {{ $t('Dashboard.UpgradeModal.PriceAmount') }}
          </span>
          <span class="sa-price-deal-row__period" aria-hidden="true">
            {{ $t('Dashboard.UpgradeModal.PricePeriod') }}
          </span>
        </p>
      </div>
      <p v-if="checkoutError" class="sa-upgrade-modal__error" role="alert">
        {{ checkoutError }}
      </p>
      <p class="sa-upgrade-stripe-footer">
        {{ $t('Dashboard.UpgradeModal.StripeFooter') }}
      </p>
    </template>

    <template v-if="step === 'checkout' && isMockCheckout" #footer>
      <button
        type="button"
        class="sa-btn-purple sa-btn-purple--block"
        :disabled="isProcessing"
        @click="confirmMockPayment"
      >
        {{ $t('Dashboard.UpgradeModal.Checkout.PayCta') }}
      </button>
    </template>

    <template v-else-if="step === 'checkout'" #footer>
      <button
        type="button"
        class="sa-btn-purple sa-btn-purple--block"
        :disabled="
          !paymentElementReady || isProcessing || !selectedPaymentMethod
        "
        @click="confirmPayment"
      >
        <SaSpinner v-if="isProcessing" size="button" />
        {{ $t('Dashboard.UpgradeModal.Checkout.PayCta') }}
      </button>
    </template>

    <template v-else-if="step === 'success'" #footer>
      <button
        type="button"
        class="sa-btn-purple sa-btn-purple--block"
        @click="finishSuccess"
      >
        {{ $t('Dashboard.UpgradeModal.Success.Done') }}
      </button>
    </template>

    <template v-else-if="step !== 'processing'" #footer>
      <button
        type="button"
        class="sa-btn-purple sa-btn-purple--block"
        @click="startCheckout"
      >
        {{ $t('Dashboard.UpgradeModal.Cta') }}
        <PaidPlanBadge />
      </button>
    </template>
  </DashboardModal>
</template>
